<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width:900px")
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          v-btn.ml-n3(text icon @click="goBack" height)
            v-icon(color="basic800" :size="24") mdi-arrow-left
        v-col.ml-3
          div.text-2xl.font-semibold.neutral700--text Add user

      v-form(ref="form")
        v-card.pa-6.mt-4.round-xl(outlined)
          div.text-lg.font-semibold.neutral700--text User roles
          div.text-sm.neutral700--text Select user roles. You are allowed to choose multiple roles
          v-card.round-xl.mt-4(outlined)
            v-row(no-gutters)
              v-col.pa-4(cols="5")
                div.text-sm.font-semibold.neutral200.neutral700--text.py-2.px-3 Admin roles
                y-checkbox.my-2(v-model="roles" value="Admin" :disabled="disableAdminRoles")
                  template(#label)
                    .text-md.neutral700--text Admin
                div.text-sm.font-semibold.neutral200.neutral700--text.py-2.px-3 Health providers
                y-checkbox.my-2(v-model="roles" value="Agent")
                  template(#label)
                    .text-md.neutral700--text Doctor & clinical staff
                div.text-sm.font-semibold.neutral200.neutral700--text.py-2.px-3 Support roles
                y-checkbox.my-2(v-model="roles" value="Salesperson")
                  template(#label)
                    .text-md.neutral700--text Clinic assistant
                //- div.text-sm.font-semibold.neutral200.neutral700--text.py-2.px-3 Finance roles
                //- y-checkbox.my-2(v-model="roles" value="Bookkeeper")
                //-   template(#label)
                //-     .text-md.neutral700--text Billings & claims
              v-divider(vertical)
              v-col.pa-4(cols="7")
                div.text-md.font-semibold.neutral700--text Role Description
                div.text-xs.neutral500--text Users will receive the combined set of permissions allowed according to their roles.
                v-divider.mt-3
                div.mt-3(v-if="roles.includes('Admin')")
                  div.text-sm.font-semibold.neutral700--text Admin
                  div.text-sm.neutral700--text This role is for people who need similar access as the account owner. This role can see and manage almost everything.
                div.mt-3(v-if="roles.includes('Agent')")
                  div.text-sm.font-semibold.neutral700--text Doctor & clinical staff
                  div.text-sm.neutral700--text Clinical Staff and Doctors can view and update patient health records and prescriptions, as well as book appointments.
                div.mt-3(v-if="roles.includes('Salesperson')")
                  div.text-sm.font-semibold.neutral700--text Clinic assistant
                  div.text-sm.neutral700--text This role can see general patient information and appointment schedules, with the ability to adjust appointments. They cannot access detailed medical data or change system settings.
                div.mt-3(v-if="roles.includes('Bookkeeper')")
                  div.text-sm.font-semibold.neutral700--text Billings & claims
                  div.text-sm.neutral700--text This role should be able to access financial data such as invoices, payments, and insurance details. They have the ability to make changes to this data, but should not have access to medical records or other sensitive information.
                div.mt-4(v-if="roles.length && !roles.includes('Admin')")
                  y-checkbox.my-2(v-model="hideClinicManagement")
                    template(#label)
                      .text-sm.neutral700--text Do not allow viewing clinic management
                  y-checkbox.my-2(v-model="hideSalesReport")
                    template(#label)
                      .text-sm.neutral700--text Do not allow viewing sales report
                  y-checkbox.my-2(v-model="allowAddUser")
                    template(#label)
                      .text-sm.neutral700--text Allow manage users
                  y-checkbox.my-2(v-model="allowCancelVisit")
                    template(#label)
                      .text-sm.neutral700--text Allow cancel visit (Registration)
        v-card.pa-6.mt-4.round-xl(v-if="roles.length" outlined)
          div.text-lg.font-semibold.neutral700--text User account
          v-row.mt-4(no-gutters)
            v-col(cols="6")
              template(v-if="roles.includes('Agent')")
                div.text-sm.font-semibold.neutral700--text {{ $t('profile_image') }}
                div.d-flex.align-center.mt-2  
                  v-avatar(size="64" color="neutral300")
                    div(v-if="resizeImageFileUrl" align="center")
                      v-img(:src="resizeImageFileUrl" contain max-height="64" max-width="64")
                        template(v-slot:placeholder)
                          v-row(class="fill-height ma-0" align="center" justify="center")
                            v-progress-circular(indeterminate color="grey")
                    v-icon(v-else color="neutral500" size="30") $yzi-user-03

                  div.file-change-layout.ml-4
                    v-btn.button-sm(color="basic700" outlined depressed) {{ $t('update') }}
                    input.input-change-layout(type="file" @change="onFileChange" accept="image/*" style="width: 92px !important")

              div.text-sm.font-semibold.neutral700--text.mt-4 First name
              v-text-field.text-sm.font-normal.mt-2.round-xl(v-model="firstName" placeholder="First name" outlined :rules="rules.not_empty" dense height="48" hide-details="auto" maxlength="30")
              div.text-sm.font-semibold.neutral700--text.mt-4 Last name
              v-text-field.text-sm.font-normal.mt-2.round-xl(v-model="lastName" placeholder="Last name" outlined :rules="rules.not_empty" dense height="48" hide-details="auto")

              template(v-if="roles.includes('Agent')")
                div.text-sm.font-semibold.neutral700--text.mt-4 {{ $t('type_of_provider') }}
                provider-type-dropdown.mt-2(v-model="type" field-class="text-sm" :error="errorProviderType" @change="removeError")
                div.text-sm.font-semibold.neutral700--text.mt-4 MMC Registration No.
                v-text-field.text-sm.font-normal.mt-2.round-xl(v-model="mmcNumber" placeholder="MMC Registration No" outlined dense height="48" hide-details="auto")
          
              div.text-sm.font-semibold.neutral700--text.mt-4 Username
              v-text-field.text-sm.mt-2.round-xl(v-model="username" placeholder="Username" outlined dense height="48" hide-details="auto" :loading="isFetchingUsername" :error-messages="userErrorMessage" @input="inputChanged" @blur="checkUsername")
                template(#append)
                  v-icon.mt-1(v-if="availableUsername === false" color="danger500") mdi-alert-circle-outline
                  v-icon.mt-1(v-if="availableUsername === true" color="success500") mdi-check-circle-outline
              div.text-sm.font-semibold.neutral700--text.mt-4 {{ $t('email') }}
              v-text-field.text-sm.mt-2.round-xl(v-model="email" :placeholder="$t('email')" outlined dense height="48" :rules="rules.email" hide-details="auto")
              div.text-sm.font-semibold.neutral700--text.mt-4 Password
              v-text-field.text-sm.mt-2.round-xl(v-model="password" placeholder="Password" outlined dense height="48" :rules="rules.password" :type="passwordType" hide-details="auto")
                template(#append)
                  v-btn(icon small style="margin-top: 2px;" @click="changePasswordType(passwordType)")
                    v-icon(color="neutral500") {{ passwordType === 'text' ? 'mdi-eye-off' : 'mdi-eye' }}
              div.text-sm.font-semibold.neutral700--text.mt-4 Confirm password
              v-text-field.text-sm.mt-2.round-xl(v-model="confirmPassword" placeholder="Confirm password" outlined dense height="48" :rules="rules.confirm_password" :type="passwordType" hide-details="auto")
                template(#append)
                  v-btn(icon small style="margin-top: 2px;" @click="changePasswordType(passwordType)")
                    v-icon(color="neutral500") {{ passwordType === 'text' ? 'mdi-eye-off' : 'mdi-eye' }}
              div.text-sm.font-semibold.neutral700--text.mt-4 Sign in link
              div.text-xs.font-semibold.neutral700--text.mt-1 Use this link to sign in to workspace
              v-text-field.text-sm.mt-2.round-xl(:value="getSignInLink()" background-color="neutral300" outlined dense height="48" readonly hide-details="auto")
                template(#append)
                  v-btn(icon small style="margin-top: 2px;" @click="copyLink" v-clipboard:copy="getSignInLink()")
                    v-icon(color="neutral500") $yzi-copy-03
        template(v-if="roles.includes('Agent') && Auth('root', 'subscribe_appointment')")
          v-card.px-6.pb-8.mt-4.round-xl(outlined)
            provider-availability    
          v-card.px-6.pb-8.mt-4.round-xl(outlined)
            provider-service

    v-footer.white(app inset padless elevation="3")
      v-container.px-8.py-2
        div.d-flex.align-center
          v-spacer
          y-button.mr-4(:label="$t('cancel')" underline color="neutral700" button-class="px-0" @click="goBack")
          y-button(:label="$t('save')" text-color="neutral100" color="neutral700" :dark="!Boolean(roles.length === 0)" :disabled="Boolean(roles.length === 0)" :loading="isLoading" @click="saveData")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { resizeImage, debounce } from '@/utils'
import ProviderAvailability from '@/components/clinic/manage/ProviderAvailability.vue'
import ProviderService from '@/components/clinic/manage/ProviderService.vue'
import ProviderTypeDropdown from '@/components/clinic/appointment/custom/ProviderTypeDropdown.vue'

export default {
  name: 'AddUser',
  components: { ProviderAvailability, ProviderService, ProviderTypeDropdown },
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isFetchingUsername: false,
      errorProviderType: false,
      hideClinicManagement: false,
      hideSalesReport: false,
      allowCancelVisit: true,
      allowAddUser: false,
      passwordType: 'text',
      role: null,
      roles: [],
      firstName: null,
      lastName: null,
      username: null,
      availableUsername: null,
      userErrorMessage: null,
      email: null,
      password: null,
      confirmPassword: null,
      type: 'Doctor',
      mmcNumber: null,
      resizeImageFileUrl: '',
      resizeImageFile: null,
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0 || `Amount should be more than ${this.$currency.symbol} 0`],
        not_empty_select: [v => !!v || 'This field is required'],
        password: [(v) => (v != null && v.toString().trim().length) || 'This field is required', (v) => (v != null && v.length >= 8) || 'Password must be at least 8 characters long'],
        confirm_password: [v => !!v || 'This field is required', v => v === this.password || 'Passwords do not match'],
        email: [ v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid', ]
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      form: 'clinic/getForm',
      tempProvider: 'clinic/getTempProvider'
    }),
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    disableAdminRoles () {
      if (!this.isAdmin) {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    checkUsername () {
      if (!this.username) {
        this.availableUsername = null
        this.userErrorMessage = null
        return
      }
      const alphanumericLowecaseMax25 = /^[a-z0-9]{1,25}$/
      if (!alphanumericLowecaseMax25.test(this.username)) {
        this.userErrorMessage = 'Alphanumeric lowercase only, max 25 characters'
        return
      }
      this.isFetchingUsername = true
      const params = {
        company: this.company.id,
        username: this.username
      }
      this.$axios.get('/members/check_username', { params })
        .then(resp => {
          this.availableUsername = resp.data.available
          if (this.availableUsername === false) {
            this.userErrorMessage = 'This username is already taken'
          } else {
            this.userErrorMessage = null
          }
        })
        .finally(() => {
          this.isFetchingUsername = false
        })
    },
    inputChanged: debounce(function () {
      this.checkUsername()
    }, 750),
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'product_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
    },
    async saveData () {
      if (this.roles.includes('Agent') && !this.type) {
        this.errorProviderType = true
      }
      if (!this.$refs.form.validate() || (this.roles.includes('Agent') && !this.type) || this.roles.length === 0) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all')])
        return
      }
      if (this.availableUsername === false) {
        this.showMessage(['error', "This username is already taken. Please choose a different username."])
        return
      }
      this.isLoading = true
      await this.$axios.get(`/users/check_email/?email=${this.email}`)
        .then(async resp => {
          if (resp.data.available) {
            let dataUser = {
              first_name: this.firstName,
              last_name: this.lastName,
              email: this.email,
              password: this.password,
              email_verified: true
            }
            await this.$axios.post('/users', dataUser)
              .catch(() => {
                this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
              })
          }
          let dataMember = {
            method: 'user_admin',
            company: this.company.id,
            roles: this.roles,
            username: this.username,
            user_email: this.email,
            app: 'clinic',
            no_clinic_management: this.roles.includes('Admin') ? false : this.hideClinicManagement,
            no_sales_report: this.roles.includes('Admin') ? false : this.hideSalesReport,
            can_cancel_visit: this.roles.includes('Admin') ? true : this.allowCancelVisit,
            can_add_user: this.roles.includes('Admin') ? true : this.allowAddUser
          }
          if (this.roles.includes('Agent')) {
            dataMember.type = this.type,
            dataMember.mmc_no = this.mmcNumber,
            dataMember.services = this.tempProvider.services,
            dataMember.schedules = this.tempProvider.schedules
            dataMember.date_overrides = this.tempProvider.date_overrides
          }
          await this.$axios.post(`/clinics/${this.company.slug}/members`, dataMember)
            .then(resp => {
              if (this.resizeImageFile) {
                var params = new FormData()
                params.append('profile_image', this.resizeImageFile)
                this.$axios.patch(`/clinics/${this.company.slug}/members/${resp.data.id}`, params)
                  .then(() => {
                    this.showMessage(['success', 'Add new user successful'])
                    this.$router.push('/admin/clinic/users')
                  })
                  .catch(() => {})
              } else {
                this.showMessage(['success', 'Add new user successful'])
                this.$router.push('/admin/clinic/users')
              }
            })
            .catch(() => {
              this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            })
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
      this.isLoading = false
    },
    changePasswordType (type) {
      if (type === 'text') {
        this.passwordType = 'password'
      } else {
        this.passwordType = 'text'
      }
    },
    getSignInLink () {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `http://${window.location.host}/signin/clinic?slug=${this.company.slug}`
      } else {
        return `https://${process.env.VUE_APP_BASE_DOMAIN}/signin/clinic?slug=${this.company.slug}`
      }
    },
    copyLink() {
      this.showMessage(['success', this.$t('success_message.link_copied_to_clipboard')])
    },
    goBack () {
      this.$router.go(-1)
    },
    removeError () {
      if (this.type) {
        this.errorProviderType = false
      }
    },
  },
  destroyed () {
    this.$store.commit('clinic/resetProvider')
    this.$store.commit('clinic/resetTempProvider')
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
</style>